import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { BiStore } from 'react-icons/bi';
import ImageGallery from 'react-image-gallery';
import { useParams } from 'react-router';

import AppDownloadButtons from '../common/components/AppDownloadButtons';
import { GutterFull } from '../common/components/layout';
import { LoadingCentered } from '../common/components/loading';
import ReviewRating from '../common/components/Rating';
import Layout from '../common/layout';
import ProfileImg from '../common/profile-img';
import { stringSplitter } from '../common/utils/strapi';
import Tastes from './components/Tastes';
import { PRODUCT_REVIEW } from './gql/product-review';

export default function ReviewViewPage() {
  const pageMeta = { title: 'Review' };
  return (
    <Layout>
      <Helmet>
        <title>{pageMeta?.title}</title>
      </Helmet>
      <Body />
    </Layout>
  );
}

export interface ImageGalleryState {
  currentIndex: number;
  currentSlideOffset: number;
  galleryWidth: number;
  isFullscreen: boolean;
  isSwipingThumbnail: boolean;
  isTransitioning: boolean;
}

function Body() {
  const [current, setCurrent] = useState<number>(0);
  const pageMeta = { title: 'Review' };
  const params = useParams<{ id: string }>();
  const [fullGallery, setFullGallery] = useState(false);

  const result = useQuery(PRODUCT_REVIEW, {
    variables: { id: params?.id },
    fetchPolicy: 'cache-first',
  });

  if (result.error) {
    return (
      <GutterFull>
        <div className='grow text-center'>
          <div className='text-9xl'>🥣</div>
          <div className='center'>
            There was an issue. Could have been deleted or un-shared.
          </div>
          <br /><br /><br /><br />
          <div className='py-5'>
            <AppDownloadButtons />
          </div>
        </div>
      </GutterFull>
    );
  }

  if (result.loading) {
    return (
      <GutterFull>
        <LoadingCentered />
      </GutterFull>
    );
  }

  // pg_graphql Relay-style: Collection → edges → node
  const node = result.data?.product_reviewsCollection?.edges?.[0]?.node;
  if (!node) {
    return (
      <GutterFull>
        <div className='grow text-center'>
          <div className='text-9xl'>🥣</div>
          <div className='center'>Review not found.</div>
        </div>
      </GutterFull>
    );
  }

  if (node) {
    pageMeta.title = `${pageMeta.title}: ${node.product_name}`;
  }

  // Images from product_review_imagesCollection
  const images = node.product_review_imagesCollection?.edges?.map((e: any) => e.node) || [];
  const items = images.map((img: any) => ({
    fullscreen: img.url || '',
    original: img.url || '',
  }));

  const profileUrl = node.profiles?.photo_url;

  const gallery = (
    <ImageGallery
      key='gallery'
      items={items}
      startIndex={current}
      showPlayButton={false}
      showNav
      showFullscreenButton={false}
      onClick={() => setFullGallery(!fullGallery)}
      lazyLoad
      onSlide={(idx) => setCurrent(idx)}
      renderItem={(item) => (
        <div className='h-full'>
          <img
            alt='thumbnail'
            src={fullGallery ? item.fullscreen : item.original}
            style={{
              height: fullGallery ? '100vh' : 400,
              width: '100%',
              objectFit: fullGallery ? 'contain' : 'cover',
            }}
          />
        </div>
      )}
    />
  );

  if (fullGallery) {
    return (
      <div className='fixed z-40 left-0 top-0 w-full h-full bg-black'>
        {gallery}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageMeta?.title}</title>
      </Helmet>
      <div className='grow pt-20'>
        {gallery}
        <div className='relative -top-8'>
          <div className='absolute flex p-3 w-full h-0 overflow-visible'>
            {items.map((_item: any, itemIdx: number) => {
              const active = itemIdx === current;
              return (
                <div key={`item_${itemIdx}`} className='shrink flex-1 h-2 opacity-50'>
                  <div
                    className={classNames(
                      'm-[1px] h-[3px] bg-white rounded-sm',
                      active ? 'opacity-100' : 'opacity-30'
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <GutterFull noTop>
          <div className='prose pt-4'>
            <h2 style={{ marginBottom: '0.6rem' }}>{node.product_name}</h2>
            <div className='relative z-10 inline-block p-2 pr-3 bg-white rounded-xl shadow-md'>
              <div className='flex flex-row items-center bg-white space-x-3'>
                <div className='flex-grow-0'>
                  <ProfileImg className='w-8 h-8' url={profileUrl} />
                </div>
                <div className='flex-grow'>
                  <div className='flex flex-col'>
                    <div className='flex-grow-0'>
                      <span className='text-gray-500 font-semibold'>
                        {node.profiles?.username}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='prose relative -top-5 ml-3 mt-3 p-3 pt-4 whitespace-pre-wrap text-xl border border-gray-100 rounded-lg shadow-sm'>
              <ReviewRating value={node.rating} />

              <Tastes
                className='my-2'
                tasteList={node.taste_list}
                withIcon
              />

              {node.store_list && node.store_list.length > 0 && (
                <div className='my-2'>
                  <BiStore className='inline' />{' '}
                  {node.store_list.split(stringSplitter).map((store: string) => (
                    <div key={store} className='badge badge-primary mr-1 text-red-500'>
                      {store}
                    </div>
                  ))}
                </div>
              )}
              <div>{node.description}</div>
            </div>

            <br />
            <div className='text-sm opacity-20'>created: {node.created_at}</div>
            <div className='text-sm opacity-20'>updated: {node.updated_at}</div>

            <AppDownloadButtons />
          </div>
        </GutterFull>
      </div>
    </>
  );
}
